// src/pages/cadastros/Procedimentos/ProcedimentosPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import CardListMobile from "../../../templates/mobile/CardListMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";

function renderCodigo(p) {
  if (p.codigo) return p.codigo;
  if (p.codigoSequencial != null) {
    return `PR${String(p.codigoSequencial).padStart(4, "0")}`;
  }
  if (p.id) return p.id.slice(0, 8);
  return "-";
}

function filtrarPorEmpresa(lista, empresaAtual, modoGlobal) {
  if (modoGlobal) return lista;
  if (!empresaAtual?.id) return lista;
  return lista.filter((p) => p.empresaId === empresaAtual.id);
}

function ProcedimentosPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [procedimentos, setProcedimentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  useEffect(() => {
    async function carregarProcedimentos() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const procedimentosRef = collection(db, "procedimentos");
        let q;

        if (usuarioLogado.grupoId) {
          if (!modoGlobal && empresaAtual?.id) {
            q = query(
              procedimentosRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("empresaId", "==", empresaAtual.id)
            );
          } else {
            q = query(
              procedimentosRef,
              where("grupoId", "==", usuarioLogado.grupoId)
            );
          }
        } else {
          q = procedimentosRef;
        }

        const snapshot = await getDocs(q);
        const lista = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        lista.sort((a, b) => {
          const codA = renderCodigo(a);
          const codB = renderCodigo(b);
          return codA.localeCompare(codB);
        });

        setProcedimentos(filtrarPorEmpresa(lista, empresaAtual, modoGlobal));
      } catch (e) {
        console.error("Erro ao carregar procedimentos:", e);
        setErro("Erro ao carregar procedimentos. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarProcedimentos();
  }, [usuarioLogado]);

  async function handleExcluir(id) {
    if (!id) return;
    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este procedimento?"
    );
    if (!confirmar) return;

    try {
      setCarregando(true);
      setErro("");

      const ref = doc(db, "procedimentos", id);
      await deleteDoc(ref);

      setProcedimentos((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error("Erro ao excluir procedimento:", e);
      setErro("Erro ao excluir procedimento. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCardsMobile = procedimentos.map((p) => ({
    id: p.id,
    nome: renderCodigo(p),
    subtitulo:
      Array.isArray(p.maquinasNomes) && p.maquinasNomes.length
        ? p.maquinasNomes.join(", ")
        : "Sem máquinas vinculadas",
    detalhes: [
      {
        icone: "🛠️",
        label: "Máquinas",
        valor:
          Array.isArray(p.maquinasNomes) && p.maquinasNomes.length
            ? p.maquinasNomes.join(", ")
            : "-",
      },
      {
        icone: "📝",
        label: "Descrição",
        valor: p.descricao || "-",
      },
      {
        icone: "📷",
        label: "Fotos",
        valor: Array.isArray(p.fotos) ? `${p.fotos.length}` : "0",
      },
      {
        icone: "📎",
        label: "Arquivos",
        valor: Array.isArray(p.arquivos) ? `${p.arquivos.length}` : "0",
      },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Procedimentos"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCardsMobile}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando procedimentos..."
          titulo="Procedimentos"
          searchPlaceholder="Buscar procedimento..."
          novoLabel="Novo Procedimento"
          emptyMessage="Nenhum procedimento encontrado"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/procedimentos/novo")}
          onEditar={(id) => navigate(`/cadastros/procedimentos/editar/${id}`)}
          onExcluir={handleExcluir}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Procedimento"
      subtitulo=""
    >
      <section className="pc-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Procedimentos cadastrados</h3>
            <p>Lista de procedimentos por máquina, vinculados ao grupo atual.</p>
          </div>

          <div className="pc-table-header-actions">
            <Can modulo="cadastros" recurso="procedimentos" acao="form">
<button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/cadastros/procedimentos/novo")}
            >
              + Novo Procedimento
            </button>
</Can>
          </div>
        </header>

        <CardListMobile
          items={itemsParaCardsMobile}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando procedimentos..."
          titulo="Procedimentos"
          searchPlaceholder="Buscar procedimento..."
          novoLabel="Novo Procedimento"
          emptyMessage="Nenhum procedimento encontrado"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/procedimentos/novo")}
          onEditar={(id) => navigate(`/cadastros/procedimentos/editar/${id}`)}
          onExcluir={handleExcluir}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </section>
    </LayoutDesktop>
  );
}

export default ProcedimentosPage;
